import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {useTheme} from 'themes'
import {getRoboyuMaxExp, useDidMount, useDidUpdate} from 'utils'
import {
  Button,
  CircularProgressBar,
  Icon,
  Modal,
  Paragraph,
} from 'common/components'
import convertUnit from 'lib/unit'
import {useSelector} from 'lib/redux'

const StyledRoboyuLevelProgressContainer = styled.div`
  display: flex;
  position: relative;
`
const StyledRowContainer = styled.div`
  display: flex;
  flex-direction: row;
`
const StyledRoboyuLevelTextContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(20)};
  margin-bottom: ${convertUnit(20)};
`
const StyledMissionItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background-color: ${({theme}) => theme.info_1};
  border-radius: ${convertUnit(28)};
  border: ${convertUnit(1)} solid ${({theme}) => theme.primary_1};
  padding: ${convertUnit(8)} ${convertUnit(12)};
  gap: ${convertUnit(8)};
  margin-top: ${convertUnit(8)};
`
const StyledModal = styled(Modal)`
  width: 100%;
  max-width: ${convertUnit(335)};
  padding: ${convertUnit(20)};
  text-align: center;
  display: flex;
  flex-direction: column;
`
const StyledRoboyuLevelProgressOverlayContainer = styled.div`
  width: ${convertUnit(72)};
  height: ${convertUnit(72)};
  display: flex;
  position: absolute;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`
const StyledRoboyuExpSeparator = styled.div`
  height: ${convertUnit(1)};
  background-color: ${({theme}) => theme.primary_4};
  width: 30%;
`
const StyledRoboyuExpMissionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`
const StyledParagraph = styled(Paragraph)`
  text-align: left;
  display: flex;
  align-items: center;
`
const StyledButton = styled(Button)`
  margin-top: ${convertUnit(20)};
`
interface GiftShopTemplateRoboyuModalExpMissionProps {
  toggleModal(): void
}
export default function GiftShopTemplateRoboyuModalExpMission({
  toggleModal,
}: GiftShopTemplateRoboyuModalExpMissionProps) {
  const theme = useTheme()
  const {translate} = useTranslation()
  const {exp, lv: level} = useSelector('roboyuState')
  const [maxExp, setMaxExp] = useState(0)
  const stateProgress = useState(0)
  const setProgress = stateProgress[1]

  interface ExpMissions {
    title: string
    expValue: number
  }
  const expMissions = useMemo<ExpMissions[]>(
    () => [
      {title: translate('giftShop:roboyuExpMissions_1'), expValue: 100},
      {title: translate('giftShop:roboyuExpMissions_2'), expValue: 2},
      {title: translate('giftShop:roboyuExpMissions_3'), expValue: 1},
    ],
    [translate],
  )

  const handleRenderExpMissionItem = useCallback(
    (index: number) => (
      <StyledMissionItem>
        <StyledParagraph color="primary_5" fontWeight="medium">
          {expMissions[index].title}
        </StyledParagraph>
        <StyledRowContainer>
          <Paragraph color="primary_5" fontWeight="medium">
            {`+${expMissions[index].expValue.toString()}`}
          </Paragraph>
          <Icon type="robopet-alt" size={14} color="primary_5" />
        </StyledRowContainer>
      </StyledMissionItem>
    ),
    [expMissions],
  )

  const handleRenderExpModal = useMemo(
    () => (
      <StyledModal visible onBackdropPress={toggleModal}>
        <StyledRoboyuLevelTextContainer>
          <StyledRoboyuLevelProgressContainer>
            <CircularProgressBar
              gradientStart={theme.info_4}
              gradientStop={theme.primary_6}
              width={72}
              strokeWidth={7}
              strokeColor={theme.gray_1}
              stateProgress={stateProgress}
            />
            <StyledRoboyuLevelProgressOverlayContainer>
              <Icon type="robopet-alt" size={16} color="primary_5" />
              <Paragraph color="primary_5" fontWeight="bold">
                {exp}
              </Paragraph>
              <StyledRoboyuExpSeparator />
              <Paragraph fontSize="xs" color="primary_4">
                {maxExp}
              </Paragraph>
            </StyledRoboyuLevelProgressOverlayContainer>
          </StyledRoboyuLevelProgressContainer>
          <StyledRoboyuExpMissionContainer>
            <StyledParagraph fontSize="l" fontWeight="bold">
              {translate('giftShop:roboyuExpMissions')}
            </StyledParagraph>
            <StyledParagraph color="info_6" fontWeight="medium">
              {translate('giftShop:roboyuExp', {
                exp: `${maxExp - exp}`,
              })}
              <Icon type="double-arrow-up" size={16} color="info_6" />
            </StyledParagraph>
          </StyledRoboyuExpMissionContainer>
        </StyledRoboyuLevelTextContainer>
        <StyledParagraph fontSize="m" fontWeight="bold">
          {translate('giftShop:roboyuExpMissions')}
        </StyledParagraph>
        {Array.from({length: 4}).map((_, idx) =>
          handleRenderExpMissionItem(idx),
        )}
        <StyledButton
          label={translate('global:close')}
          color="primary_5"
          backgroundColor="white_3"
          onClick={toggleModal}
        />
      </StyledModal>
    ),
    [
      exp,
      handleRenderExpMissionItem,
      maxExp,
      stateProgress,
      theme.gray_1,
      theme.info_4,
      theme.primary_6,
      toggleModal,
      translate,
    ],
  )

  useDidMount(() => {
    setMaxExp(getRoboyuMaxExp(level || 1))
  })

  useDidUpdate(() => {
    if (maxExp > 0) {
      setProgress((exp / maxExp) * 100)
    }
  }, [maxExp])

  return handleRenderExpModal
}
